import React, { useState, useEffect, useMemo } from 'react';
import { Search, UserCheck, Trash2, Loader2, Plus, User } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { fetchClients } from '@/pages/parametrizacion/clients/Services/clients.services';
import { asignarClientesConductor } from '@/pages/parametrizacion/usuarios/Services/users.services';

/**
 * Modal para asignar uno o varios clientes a un usuario con rol `cliente_prendas`.
 * Muestra el catálogo completo de clientes activos (de cualquier lavandería)
 * para que un mismo usuario pueda supervisar varios clientes,
 * incluso de centros de lavado distintos.
 *
 * Reutiliza el endpoint `PUT /user/conductores/:id/clientes` (que ya soporta
 * el array `clientesAsignados: [{ id, nombre, nit? }]` en el modelo User).
 */
const AsignarClientePrendasModal = ({ isOpen, onClose, usuario, onSuccess }) => {
  const { toast } = useToast();
  const [allClients, setAllClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');
  // Lista de clientes asignados (cada item: { id, nombre, nit?, lavanderia? })
  const [asignados, setAsignados] = useState([]);

  // Carga inicial: clientes pre-asignados + catálogo completo de clientes activos
  useEffect(() => {
    if (isOpen && usuario) {
      setAsignados(usuario.clientesAsignados || []);
      setSearch('');
      loadClients();
    }
  }, [isOpen, usuario]);

  const loadClients = async () => {
    setIsLoadingClients(true);
    try {
      // Catálogo global: clientes activos de TODAS las lavanderías.
      const res = await fetchClients({ limit: 1000, offset: 1, query: { estado: 'Activo' } });
      setAllClients(res.data || []);
    } catch {
      toast({ title: 'Error', description: 'No se pudieron cargar los clientes.', variant: 'destructive' });
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Clientes disponibles = activos - ya asignados, con búsqueda opcional
  const clientesDisponibles = useMemo(() => {
    const asignadosIds = new Set(asignados.map((c) => c.id));
    return allClients.filter((c) => {
      const noAsignado = !asignadosIds.has(c._id);
      const t = search.toLowerCase();
      const coincide =
        !t ||
        c.nombre?.toLowerCase().includes(t) ||
        c.nit?.toLowerCase().includes(t) ||
        c.lavanderia?.nombre?.toLowerCase().includes(t);
      return noAsignado && coincide;
    });
  }, [allClients, asignados, search]);

  const handleAgregar = (cliente) => {
    setAsignados((prev) => [
      ...prev,
      {
        id: cliente._id,
        nombre: cliente.nombre,
        nit: cliente.nit || undefined,
        lavanderia: cliente.lavanderia?.nombre || undefined,
      },
    ]);
  };

  const handleRemover = (clienteId) => {
    setAsignados((prev) => prev.filter((c) => c.id !== clienteId));
  };

  const handleGuardar = async () => {
    setIsSaving(true);
    try {
      await asignarClientesConductor(usuario._id, asignados);
      const total = asignados.length;
      toast({
        title: 'Éxito',
        description: total === 0
          ? `Se quitaron los clientes de ${usuario.nombre}.`
          : total === 1
            ? `Cliente "${asignados[0].nombre}" asignado a ${usuario.nombre}.`
            : `${total} clientes asignados a ${usuario.nombre} correctamente.`,
      });
      onSuccess?.();
      onClose();
    } catch {
      toast({ title: 'Error', description: 'No se pudo guardar la asignación.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <UserCheck className="h-5 w-5 text-primary" />
            Asignar Clientes — <span className="text-primary">{usuario?.nombre}</span>
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Selecciona los clientes que este usuario podrá ver en el Portal del Cliente Prendas.
            Los cambios se guardan al presionar &quot;Guardar asignación&quot;.
          </p>
        </DialogHeader>

        <div className="flex flex-1 gap-0 overflow-hidden min-h-0">
          {/* Panel izquierdo: clientes disponibles */}
          <div className="flex flex-col w-1/2 border-r border-border overflow-hidden">
            <div className="px-5 pt-4 pb-3 shrink-0">
              <p className="text-sm font-medium mb-2">
                Clientes disponibles
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  ({clientesDisponibles.length})
                </span>
              </p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, NIT o lavandería..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-4 custom-scrollbar">
              {isLoadingClients ? (
                <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando clientes...
                </div>
              ) : clientesDisponibles.length === 0 ? (
                <p className="text-sm text-muted-foreground italic text-center mt-8">
                  {allClients.length === 0
                    ? 'No hay clientes activos en el sistema'
                    : 'Todos los clientes ya están asignados o no coincide la búsqueda'}
                </p>
              ) : (
                <ul className="space-y-1">
                  {clientesDisponibles.map((cliente) => (
                    <li key={cliente._id}>
                      <button
                        onClick={() => handleAgregar(cliente)}
                        className="w-full text-left px-3 py-2.5 rounded-md border border-border/50 hover:border-primary/50 hover:bg-primary/5 transition-colors flex items-center justify-between group"
                      >
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-medium truncate">{cliente.nombre}</span>
                          <span className="text-xs text-muted-foreground truncate">
                            {cliente.nit ? `NIT: ${cliente.nit}` : ''}
                            {cliente.nit && cliente.lavanderia?.nombre ? ' · ' : ''}
                            {cliente.lavanderia?.nombre || ''}
                          </span>
                        </div>
                        <Plus className="h-4 w-4 text-primary shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ml-2" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Panel derecho: clientes asignados */}
          <div className="flex flex-col w-1/2 overflow-hidden">
            <div className="px-5 pt-4 pb-3 shrink-0">
              <p className="text-sm font-medium mb-2">
                Asignados al usuario
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  ({asignados.length})
                </span>
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-4 custom-scrollbar">
              {asignados.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-sm text-muted-foreground text-center border border-dashed border-border rounded-md mt-2 p-4">
                  <User className="h-8 w-8 mb-2 opacity-30" />
                  Sin clientes asignados.
                  <br />
                  Selecciona del panel izquierdo.
                </div>
              ) : (
                <ul className="space-y-1">
                  {asignados.map((c) => (
                    <li
                      key={c.id}
                      className="flex items-center justify-between px-3 py-2.5 bg-primary/5 rounded-md border border-primary/20"
                    >
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium truncate">{c.nombre}</span>
                        <span className="text-xs text-muted-foreground truncate">
                          {c.nit ? `NIT: ${c.nit}` : ''}
                          {c.nit && c.lavanderia ? ' · ' : ''}
                          {c.lavanderia || ''}
                        </span>
                      </div>
                      <button
                        onClick={() => handleRemover(c.id)}
                        className="text-red-400 hover:text-red-600 transition-colors shrink-0 ml-2"
                        title="Quitar cliente"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Guardar asignación
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AsignarClientePrendasModal;
